import { SUPPORT_EMAIL } from '@fruitful/member-profile/src/common/types/Support';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, router } from 'expo-router';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    ControlledInput,
    Form,
    LabelRegular,
    ScreenContainer,
    Spinner,
    tokens,
    USE_FORM_MODE_DEFAULTS,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { useSendEmailToSupport } from '../../common/hooks/useSendEmailToSupport';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useUpsertAddress from '../../user/hooks/useUpsertAddress';
import { useUser } from '../../user/providers/UserProvider';
import { AddressType } from '../../user/types/User';
import {
    SUCCESS_TOAST_DURATION,
    SUCCESS_TOAST_MESSAGE,
    SuccessToastIcon,
} from './SuccessToast';

const addressSchema = z.object({
    line1: z.string().min(1, 'Street address is required'),
    line2: z.string().optional(),
    city: z.string().min(1, 'City is required'),
    state: z.string().length(2, 'State must be 2 characters'),
    postalCode: z.string().length(5, 'ZIP code must be 5 digits'),
});

type AddressSchema = z.infer<typeof addressSchema>;

export const Address = () => {
    LogRocketFacade.tagPage('Profile/Address');

    const toast = useToastController();
    const sendEmailToSupport = useSendEmailToSupport();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const authedUser = useAuthedUser();
    const { userAddress, syncteraKycStatus } = authedUser;

    // Once a user submits their address to synctera via KYC, it's no longer editable without contacting HD to do fraud checks
    const isAddressEditDisabled = syncteraKycStatus !== null;

    const { refetch: refetchUser } = useUser();
    const { mutateAsync: upsertAddress, isPending: isUpsertingAddress } =
        useUpsertAddress();

    const {
        handleSubmit,
        formState: { isValid, errors },
        register,
        control,
    } = useForm<AddressSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(addressSchema),
        defaultValues: {
            line1: userAddress?.line1 ?? '',
            line2: userAddress?.line2 ?? '',
            city: userAddress?.city ?? '',
            state: userAddress?.state ?? '',
            postalCode: userAddress?.postalCode ?? '',
        },
    });

    const onSubmit: SubmitHandler<AddressSchema> = useCallback(
        async (data) => {
            setIsSubmitting(true);
            try {
                await upsertAddress({
                    body: {
                        id: userAddress?.id,
                        userId: authedUser.id,
                        type: AddressType.LEGAL,
                        line1: data.line1,
                        line2: data.line2 ?? undefined,
                        city: data.city,
                        state: data.state ?? undefined,
                        postalCode: data.postalCode,
                        countryCode: 'US',
                    },
                });
                toast.show(SUCCESS_TOAST_MESSAGE, {
                    duration: SUCCESS_TOAST_DURATION,
                    icon: SuccessToastIcon,
                });
                void refetchUser?.();
                router.back();
            } catch (error) {
                captureException(error);
                toast.show('Something went wrong!', { duration: 2000 });
            } finally {
                setIsSubmitting(false);
            }
        },
        [authedUser, userAddress?.id, upsertAddress, toast, refetchUser],
    );

    const isFormSubmitting = isSubmitting || isUpsertingAddress;

    return (
        <ScreenContainer title="Home address" hasBackButton={true}>
            {isFormSubmitting ? (
                <Spinner />
            ) : (
                <YStack gap={24} justifyContent="space-between">
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <YStack gap={16}>
                            <ControlledInput
                                {...register('line1')}
                                label="Street address"
                                control={control}
                                register={register}
                                errors={errors}
                                autoComplete="address-line1"
                                textContentType="streetAddressLine1"
                                disabled={isAddressEditDisabled}
                            />

                            <ControlledInput
                                {...register('line2')}
                                label="Apartment, suite, etc. (optional)"
                                control={control}
                                register={register}
                                errors={errors}
                                autoComplete="address-line2"
                                textContentType="streetAddressLine2"
                                disabled={isAddressEditDisabled}
                            />

                            <ControlledInput
                                {...register('city')}
                                label="City"
                                control={control}
                                register={register}
                                errors={errors}
                                textContentType="addressCity"
                                disabled={isAddressEditDisabled}
                            />

                            <XStack gap={12}>
                                <View flex={1}>
                                    <ControlledInput
                                        {...register('state')}
                                        label="State"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        textContentType="addressState"
                                        maxLength={2}
                                        autoCapitalize="characters"
                                        disabled={isAddressEditDisabled}
                                    />
                                </View>
                                <View flex={1}>
                                    <ControlledInput
                                        {...register('postalCode')}
                                        label="ZIP"
                                        control={control}
                                        register={register}
                                        errors={errors}
                                        autoComplete="postal-code"
                                        textContentType="postalCode"
                                        maxLength={5}
                                        type="numeric"
                                        inputMode="numeric"
                                        disabled={isAddressEditDisabled}
                                    />
                                </View>
                            </XStack>
                            {isAddressEditDisabled && (
                                <LabelRegular color="$gray1000">
                                    If you need to edit your home address,
                                    please contact{' '}
                                    <Link
                                        href={`mailto:${SUPPORT_EMAIL}?subject=Address change`}
                                        style={{
                                            fontSize: 12, // Hardcoded as a workaround for RNW not setting the right font size.
                                            textDecorationLine: 'underline',
                                            color: tokens.color.light.gray
                                                .gray1000,
                                        }}
                                        onPress={(event) => {
                                            event.preventDefault();
                                            void sendEmailToSupport({
                                                subject: 'Address change',
                                            });
                                        }}
                                    >
                                        {SUPPORT_EMAIL}
                                    </Link>
                                </LabelRegular>
                            )}
                        </YStack>
                    </Form>

                    <Button
                        variant={ButtonVariant.PRIMARY}
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSubmit(onSubmit)}
                        disabled={
                            !isValid ||
                            isFormSubmitting ||
                            isAddressEditDisabled
                        }
                    />
                </YStack>
            )}
        </ScreenContainer>
    );
};

export default Address;
